import { supabase } from './supabase';

export interface GalleryImage {
  id: string;
  url: string;
  type: 'moodboard' | 'upstaging' | 'touchup';
  createdAt: string;
  prompt?: string;
}

export const imageGalleryService = {
  // Test function to check database connectivity
  async testDatabaseConnection(): Promise<void> {
    try {
      console.log('Testing database connection...');
      
      // Test moodboard table
      const { data: moodboardTest, error: moodboardError } = await supabase
        .from('moodboard_generations')
        .select('count')
        .limit(1);
      
      console.log('Moodboard test result:', { data: moodboardTest, error: moodboardError });
      
      // Test upstaging table
      const { data: upstagingTest, error: upstagingError } = await supabase
        .from('upstaging_generations')
        .select('count')
        .limit(1);
      
      console.log('Upstaging test result:', { data: upstagingTest, error: upstagingError });
      
      // Test ai_touchup table
      const { data: touchupTest, error: touchupError } = await supabase
        .from('ai_touchup_operations')
        .select('count')
        .limit(1);
      
      console.log('Touchup test result:', { data: touchupTest, error: touchupError });
      
    } catch (error) {
      console.error('Database connection test failed:', error);
    }
  },

  // Fetch all images from all tables
  async getAllImages(): Promise<GalleryImage[]> {
    try {
      const images: GalleryImage[] = [];

      // Fetch moodboard generations
      console.log('Fetching moodboard generations...');
      const { data: moodboardGenerations, error: moodboardError } = await supabase
        .from('moodboard_generations')
        .select('id, result_image_url, created_at')
        .not('result_image_url', 'is', null)
        .eq('status', 'completed');

      if (moodboardError) {
        console.error('Error fetching moodboard images:', moodboardError);
      } else if (moodboardGenerations) {
        console.log('Moodboard generations found:', moodboardGenerations.length);
        moodboardGenerations.forEach(gen => {
          if (gen.result_image_url) {
            images.push({
              id: gen.id,
              url: gen.result_image_url,
              type: 'moodboard',
              createdAt: gen.created_at
            });
          }
        });
      }

      // Fetch upstaging generations (both original and upstaged)
      console.log('Fetching upstaging generations...');
      const { data: upstagingGenerations, error: upstagingError } = await supabase
        .from('upstaging_generations')
        .select('id, original_image_url, upstaged_image_url, created_at, prompt');

      if (upstagingError) {
        console.error('Error fetching upstaging images:', upstagingError);
      } else if (upstagingGenerations) {
        console.log('Upstaging generations found:', upstagingGenerations.length);
        upstagingGenerations.forEach(gen => {
          if (gen.original_image_url) {
            images.push({
              id: `${gen.id}-original`,
              url: gen.original_image_url,
              type: 'upstaging',
              createdAt: gen.created_at,
              prompt: gen.prompt
            });
          }
          if (gen.upstaged_image_url) {
            images.push({
              id: `${gen.id}-upstaged`,
              url: gen.upstaged_image_url,
              type: 'upstaging',
              createdAt: gen.created_at,
              prompt: gen.prompt
            });
          }
        });
      }

      // Fetch AI touchup operations
      console.log('Fetching AI touchup operations...');
      const { data: touchupOperations, error: touchupError } = await supabase
        .from('ai_touchup_operations')
        .select('id, original_image_url, result_image_url, created_at, prompt')
        .eq('processing_status', 'completed');

      if (touchupError) {
        console.error('Error fetching touchup images:', touchupError);
      } else if (touchupOperations) {
        console.log('Touchup operations found:', touchupOperations.length);
        touchupOperations.forEach(op => {
          if (op.original_image_url) {
            images.push({
              id: `${op.id}-original`,
              url: op.original_image_url,
              type: 'touchup',
              createdAt: op.created_at,
              prompt: op.prompt
            });
          }
          if (op.result_image_url) {
            images.push({
              id: `${op.id}-result`,
              url: op.result_image_url,
              type: 'touchup',
              createdAt: op.created_at,
              prompt: op.prompt
            });
          }
        });
      }

      console.log('Total images collected:', images.length);

      // Sort by creation date (newest first) and remove duplicates
      const uniqueImages = images.filter((image, index, self) => 
        index === self.findIndex(img => img.url === image.url)
      );

      console.log('Unique images after deduplication:', uniqueImages.length);

      return uniqueImages.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      console.error('Error fetching all images:', error);
      return [];
    }
  },

  // Get just the image URLs for GridMotion
  async getImageUrls(): Promise<string[]> {
    const images = await this.getAllImages();
    return images.map(img => img.url);
  },

  // Get a mix of images and text items for GridMotion
  async getGridMotionItems(): Promise<string[]> {
    // Use Lorem Picsum to generate random beautiful images
    const items: string[] = Array.from({ length: 28 }, (_, index) => {
      // Generate random images with different sizes and effects for variety
      const width = 400;
      const height = 300;
      const randomSeed = Math.floor(Math.random() * 1000);
      
      // Add some variety with different effects
      const effects = [
        '', // No effect
        '?grayscale',
        '?blur=1',
        '?blur=2',
        '?grayscale&blur=1'
      ];
      
      const randomEffect = effects[Math.floor(Math.random() * effects.length)];
      
      return `https://picsum.photos/seed/${randomSeed}/${width}/${height}${randomEffect}`;
    });
    
    console.log('GridMotion items created:', items.length, 'items using Lorem Picsum images');
    return items;
  }
}; 